import styled from '@emotion/styled'
import { Box, Typography } from '@mui/material'
import isEmpty from 'lodash.isempty'
import React from 'react'

import { formatRating } from '@js/common/utils'

import { CTAButton } from '@f-fertighaus/applications/Next/components/common/CTAButton'
import { MobileOnly } from '@f-fertighaus/applications/Next/components/common/MediaQuery'
import { HdpNavigationTab } from '@f-fertighaus/applications/Next/components/common/navigation/tabs/useActiveTabs'
import { RightLinkArrowIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/RightLinkArrowIcon'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { PRICE } from '@f-fertighaus/applications/Next/utils/price'
import { Rating as StarRating } from '@f-fertighaus/components/ReviewRating/SearchResultRating'

import { useActiveTabs } from '../../components/common/navigation/tabs/useActiveTabs'
import { Domain } from '../../pages/house-detail-page/HDPContent'
import {
  HouseBasicInformation,
  HouseOverviewContainer,
  VendorLogoContainer
} from './HouseOverview.styled'

const IM_DOMAINS: Domain[] = ['bauen', 'immonet', 'immowelt']

export const HouseOverview = () => {
  const { state: data, reviewsActive } = useHouseDetail()
  const { handleTabChange } = useActiveTabs()
  const { avg_rating, count_total } = data.review_data || {}

  return (
    <>
      <HouseOverviewContainer>
        <HouseBasicInformation>
          <Typography component='h1'>
            <HeadingContentContainer>
              <HouseTitle />
            </HeadingContentContainer>
          </Typography>
          <MobileOnly>
            <Typography variant='mediumTitle' component='p'>
              {data.price_display
                ? t('houseDetailPage.houseOverview.price', {
                    price: PRICE.formatPrice(data.price_display)
                  })
                : t('houseDetailPage.houseOverview.price-missing')}
            </Typography>
          </MobileOnly>
        </HouseBasicInformation>
        <MobileOnly>
          <VendorLogoContainer
            onClick={() => {
              handleTabChange(HdpNavigationTab.vendor)
            }}>
            <img
              style={{ aspectRatio: '3 / 2', objectFit: 'contain' }}
              src={data.vendor_verbose.logo['max-320x240.noauto']}
              alt={t('houseDetailPage.houseOverview.vendorLogoAltText', {
                vendorName: data.vendor_verbose.display_name
              })}
            />
          </VendorLogoContainer>
        </MobileOnly>
      </HouseOverviewContainer>
      {!isEmpty(data.review_data) && reviewsActive && (
        <MobileOnly>
          <VendorRatingOverview>
            <Typography
              variant='body2'
              component='p'
              fontWeight='fontWeightBold'>
              {t('houseDetailPage.vendor.reviews-section', {
                vendorName: data.vendor_verbose.display_name
              })}
            </Typography>
            <RatingStars>
              <StarsWrapper>
                <StarRating value={avg_rating} size={32} />
              </StarsWrapper>
              <Typography variant='mediumTitle' component='p' marginLeft={2}>
                {formatRating(avg_rating)}
              </Typography>
              <Typography
                variant='subtitle1'
                marginLeft={1}
                color='primary.light'>
                {`(${count_total})`}
              </Typography>
            </RatingStars>
            <CTAButton
              variant='text'
              color='secondary'
              sx={{ padding: 0 }}
              endIcon={
                <Box sx={{ transform: 'rotate(90deg)' }}>
                  <RightLinkArrowIcon />
                </Box>
              }
              onClick={() => handleTabChange(HdpNavigationTab.vendor)}>
              <Box fontWeight='fontWeightRegular'>
                {t('houseDetailPage.vendor.scroll-to-section')}
              </Box>
            </CTAButton>
          </VendorRatingOverview>
        </MobileOnly>
      )}
    </>
  )
}

const HouseTitle = () => {
  const { state: data, domain } = useHouseDetail()

  const INTENTIONAL_SPACE = ' '

  if (domain === 'massivhaus') {
    return `${data.vendor_verbose.display_name}: ${data.name}`
  }

  if (IM_DOMAINS.includes(domain)) {
    const houseNameWithVendorName = `${data.name} von ${data.vendor_verbose.display_name}`
    return data.utilization_name
      ? `${data.utilization_name} ${houseNameWithVendorName}`
      : houseNameWithVendorName
  }

  return (
    <>
      {data.name + INTENTIONAL_SPACE}
      <StyledTypography lineHeight={1.2} component='span'>
        {t('houseDetailPage.houseOverview.fromVendor', {
          vendorName: data.vendor_verbose.display_name
        })}
      </StyledTypography>
    </>
  )
}

const HeadingContentContainer = styled(Box)`
  font-family: ${({ theme }) => theme.font.base};
  font-size: 14px;
  line-height: 1.2;
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-bottom: ${({ theme }) => theme.spacing(2)};
    font-family: ${({ theme }) => theme.font.fancy};
    font-size: 32px;
  }
`

const StyledTypography = styled(Typography)`
  display: inline-block;
  font-family: ${({ theme }) => theme.font.base};
  font-size: 14px;
  line-height: 1.2;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-family: ${({ theme }) => theme.font.fancy};
    font-size: 32px;
    line-height: 1.2;
  }
` as typeof Typography

const RatingStars = styled.div`
  display: flex;
  align-items: center;
`

const StarsWrapper = styled.div`
  display: flex;
  gap: 4px;
`

const VendorRatingOverview = styled.div`
  padding: 16px;
  margin: 0 -16px;
  background: #fff;
  border-bottom: 2px solid #d5d1d1;
`
